import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, DollarSign, CheckCircle, Clock, Eye, Landmark } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Select } from '../ui/Select';
import { balanceConsolidadoService } from '../../services/balanceConsolidadoService';
import { PeriodoBalance, EvolucionMensualBalance } from '../../services/balanceService';
import { supabase } from '../../lib/supabase';
import { EvolucionAnualTable } from '../EvolucionAnualTable';
import { nivelPanelBalance, type NivelPanelBalance } from '../../utils/permissions';
import { useAlCambiarClave } from '../../hooks/useAlCambiarClave';
import type { Usuario } from '../../types/auth';

interface BalanceConsolidadoViewProps {
  readonly onVerDesglose: (entidadId: string, tipoEntidad: 'lumisial' | 'diocesis', periodo: PeriodoBalance) => void;
  readonly initialMes?: number;
  readonly initialAño?: number;
}

interface BalanceConsolidado {
  total_ingresos: number;
  total_egresos: number;
  /** Entradas por financiamiento de deudas: fuera de los ingresos, dentro del balance. */
  total_financiamiento: number;
  balance_neto: number;
  entidades_notificadas: number;
  total_entidades: number;
  entidades: Array<{
    entidad_id: string;
    entidad_nombre: string;
    ingresos: number;
    egresos: number;
    financiamiento: number;
    balance: number;
    ha_notificado_balance: boolean;
  }>;
}

const BALANCE_VACIO: BalanceConsolidado = {
  total_ingresos: 0,
  total_egresos: 0,
  total_financiamiento: 0,
  balance_neto: 0,
  entidades_notificadas: 0,
  total_entidades: 0,
  entidades: [],
};

/**
 * Entidad cuyo consolidado se muestra: la diócesis propia (sus Lumisiales) o una provincia (sus
 * diócesis), como `tipo:id`. Texto para usarla como dependencia estable de los efectos; vacío si
 * todavía no hay entidad (admin_global sin provincia elegida).
 */
function claveEntidadConsolidado(
  usuario: Usuario | null,
  nivelPanel: NivelPanelBalance | null,
  provinciaSeleccionada: string
): string {
  if (nivelPanel === 'diocesano' && usuario?.diocesis_id) return `diocesis:${usuario.diocesis_id}`;
  if (nivelPanel === 'nacional' && usuario?.provincia_eclesiastica_id) {
    return `provincia:${usuario.provincia_eclesiastica_id}`;
  }
  if (nivelPanel === 'global' && provinciaSeleccionada) return `provincia:${provinciaSeleccionada}`;
  return '';
}

function consultarBalanceConsolidado(claveEntidad: string, mes: number, año: number): Promise<BalanceConsolidado> {
  const [tipo, id] = claveEntidad.split(':');
  return tipo === 'diocesis'
    ? balanceConsolidadoService.getBalanceDiocesano(id, mes, año)
    : balanceConsolidadoService.getBalanceNacional(id, mes, año);
}

async function cargarProvinciasActivas(): Promise<Array<{ id: string; nombre: string }>> {
  const { data, error } = await supabase
    .from('provincias_eclesiasticas')
    .select('id, nombre')
    .eq('activo', true)
    .order('nombre');
  if (error) throw error;
  return data ?? [];
}

export const BalanceConsolidadoView: React.FC<BalanceConsolidadoViewProps> = ({ onVerDesglose, initialMes, initialAño }) => {
  const { usuario } = useAuth();
  // La JAD y la JAN ven el consolidado de su nivel igual que su Junta Sacerdotal.
  const nivelPanel = nivelPanelBalance(usuario);
  const [loading, setLoading] = useState(true);
  const [mes, setMes] = useState(initialMes ?? new Date().getMonth() + 1);
  const [año, setAño] = useState(initialAño ?? new Date().getFullYear());
  const [provinciasEclesiasticas, setProvinciasEclesiasticas] = useState<Array<{ id: string; nombre: string }>>([]);
  const [provinciaSeleccionada, setProvinciaSeleccionada] = useState<string>('');
  const [balance, setBalance] = useState<BalanceConsolidado>(BALANCE_VACIO);

  const [evolucion, setEvolucion] = useState<EvolucionMensualBalance[]>([]);
  const [loadingEvolucion, setLoadingEvolucion] = useState(false);

  const años = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const meses = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' }
  ];

  const claveEntidad = claveEntidadConsolidado(usuario, nivelPanel, provinciaSeleccionada);

  // Cambiar de entidad o de período vuelve a mostrar la carga.
  useAlCambiarClave(`${claveEntidad}|${mes}|${año}`, () => {
    setLoading(claveEntidad !== '');
    setBalance(BALANCE_VACIO);
  });
  useAlCambiarClave(`${claveEntidad}|${año}`, () => { setLoadingEvolucion(claveEntidad !== ''); });

  useEffect(() => {
    if (nivelPanel !== 'global') return;
    let vigente = true;
    cargarProvinciasActivas()
      .then(provincias => {
        if (!vigente) return;
        setProvinciasEclesiasticas(provincias);
        setProvinciaSeleccionada(actual => actual || provincias[0]?.id || '');
      })
      .catch((error: unknown) => { console.error('Error loading provincias:', error); });
    return () => { vigente = false; };
  }, [nivelPanel]);

  useEffect(() => {
    if (!claveEntidad) return;
    let vigente = true;
    consultarBalanceConsolidado(claveEntidad, mes, año)
      .then(balanceData => { if (vigente) setBalance(balanceData); })
      .catch((error: unknown) => { console.error('Error loading balance consolidado:', error); })
      .finally(() => { if (vigente) setLoading(false); });
    return () => { vigente = false; };
  }, [claveEntidad, mes, año]);

  useEffect(() => {
    if (!claveEntidad) return;
    const [tipo, id] = claveEntidad.split(':');
    let vigente = true;
    balanceConsolidadoService.getEvolucionAnualConsolidada(id, tipo as 'diocesis' | 'provincia', año)
      .then(data => { if (vigente) setEvolucion(data); })
      .catch((error: unknown) => { console.error('Error loading evolución consolidada:', error); })
      .finally(() => { if (vigente) setLoadingEvolucion(false); });
    return () => { vigente = false; };
  }, [claveEntidad, año]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(amount);
  };

  const getTipoEntidad = (): 'lumisial' | 'diocesis' => {
    return nivelPanel === 'diocesano' ? 'lumisial' : 'diocesis';
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {nivelPanel === 'global' && (
            <div className="flex-1">
              <Select
                label="Provincia Eclesiástica"
                options={provinciasEclesiasticas.map((p) => ({ value: p.id, label: p.nombre }))}
                value={provinciaSeleccionada}
                onChange={(v) => setProvinciaSeleccionada(v)}
                placeholder="Seleccionar..."
              />
            </div>
          )}

          <div className="flex-1">
            <Select
              label="Mes"
              options={meses.map((m) => ({ value: String(m.value), label: m.label }))}
              value={String(mes)}
              onChange={(v) => setMes(Number(v))}
              placeholder=""
            />
          </div>

          <div className="flex-1">
            <Select
              label="Año"
              options={años.map((a) => ({ value: String(a), label: String(a) }))}
              value={String(año)}
              onChange={(v) => setAño(Number(v))}
              placeholder=""
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
        </div>
      ) : (
        <>
          <div className={`grid grid-cols-1 gap-4 ${balance.total_financiamiento > 0 ? 'md:grid-cols-3 xl:grid-cols-5' : 'md:grid-cols-4'}`}>
            <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
                <span className="text-sm font-medium text-slate-600">Total Ingresos</span>
              </div>
              <p className="text-2xl font-bold text-slate-800">{formatCurrency(balance.total_ingresos)}</p>
            </div>

            <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown className="w-5 h-5 text-red-600" />
                <span className="text-sm font-medium text-slate-600">Total Egresos</span>
              </div>
              <p className="text-2xl font-bold text-slate-800">{formatCurrency(balance.total_egresos)}</p>
            </div>

            {balance.total_financiamiento > 0 && (
              <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Landmark className="w-5 h-5 text-teal-600" />
                  <span className="text-sm font-medium text-slate-600">Financiamiento recibido</span>
                </div>
                <p className="text-2xl font-bold text-slate-800">{formatCurrency(balance.total_financiamiento)}</p>
              </div>
            )}

            <div className={`rounded-xl shadow-md border p-6 ${
              balance.balance_neto >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className={`w-5 h-5 ${balance.balance_neto >= 0 ? 'text-emerald-600' : 'text-red-600'}`} />
                <span className="text-sm font-medium text-slate-600">Balance Neto</span>
              </div>
              <p className={`text-2xl font-bold ${balance.balance_neto >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                {formatCurrency(balance.balance_neto)}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-slate-600">Notificaciones</span>
              </div>
              <p className="text-2xl font-bold text-slate-800">
                {balance.entidades_notificadas} / {balance.total_entidades}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-xl font-bold text-slate-800">
                {nivelPanel === 'diocesano' ? 'Lumisiales' : 'Diócesis'}
              </h3>
            </div>

            {/* Desktop */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Entidad
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Ingresos
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Egresos
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Balance
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {balance.entidades.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-slate-500">
                        No hay datos para el período seleccionado
                      </td>
                    </tr>
                  ) : (
                    balance.entidades.map((entidad) => (
                      <tr key={entidad.entidad_id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-slate-800">
                          {entidad.entidad_nombre}
                        </td>
                        <td className="px-6 py-4 text-sm text-right text-emerald-600 font-semibold">
                          {formatCurrency(entidad.ingresos)}
                        </td>
                        <td className="px-6 py-4 text-sm text-right text-red-600 font-semibold">
                          {formatCurrency(entidad.egresos)}
                        </td>
                        <td className={`px-6 py-4 text-sm text-right font-bold ${
                          entidad.balance >= 0 ? 'text-emerald-700' : 'text-red-700'
                        }`}>
                          {formatCurrency(entidad.balance)}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {entidad.ha_notificado_balance ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                              <CheckCircle className="w-3 h-3" />
                              Notificado
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
                              <Clock className="w-3 h-3" />
                              Pendiente
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={() => onVerDesglose(entidad.entidad_id, getTipoEntidad(), { tipo: 'mensual', año, mes })}
                            className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                            Ver Desglose
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile */}
            <div className="sm:hidden divide-y divide-slate-200">
              {balance.entidades.length === 0 ? (
                <p className="px-4 py-8 text-center text-slate-500">No hay datos para el período seleccionado</p>
              ) : (
                balance.entidades.map((entidad) => (
                  <div key={entidad.entidad_id} className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-semibold text-slate-800">{entidad.entidad_nombre}</span>
                      {entidad.ha_notificado_balance ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 shrink-0">
                          <CheckCircle className="w-3 h-3" />
                          Notificado
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 shrink-0">
                          <Clock className="w-3 h-3" />
                          Pendiente
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                      <div>
                        <p className="text-xs text-slate-500">Ingresos</p>
                        <p className="font-semibold text-emerald-600">{formatCurrency(entidad.ingresos)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Egresos</p>
                        <p className="font-semibold text-red-600">{formatCurrency(entidad.egresos)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Balance</p>
                        <p className={`font-bold ${entidad.balance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                          {formatCurrency(entidad.balance)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => onVerDesglose(entidad.entidad_id, getTipoEntidad(), { tipo: 'mensual', año, mes })}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200"
                    >
                      <Eye className="w-4 h-4" />
                      Ver Desglose
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
          <EvolucionAnualTable
            datos={evolucion}
            loading={loadingEvolucion}
            año={año}
            mesSeleccionado={mes}
            onMesClick={(m) => setMes(m)}
          />
        </>
      )}
    </div>
  );
};
